import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
} from "recharts";
import { NetworthChartCard } from "../components/networth/NetworthChartCard";
import { useAccountSummary } from "../hooks/accounts";
import { useAllocation } from "../hooks/allocation";
import { useCashflowSummary } from "../hooks/cashflow";
import { useInvestmentsSummary } from "../hooks/investments";
import { fetchOtterfolioOverview } from "../lib/otterfolio";
import { type NetworthPeriod, useNetworthHistory, useNetworthSummary } from "../hooks/networth";
import { usePortfolioSummary as useRealEstatePortfolioSummary } from "../hooks/realestate";
import { useIsMobile } from "../hooks/useIsMobile";
import { fetchMe } from "../lib/auth";

// ============== Helpers ==============
const NF_EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

function fmtEur(cents: number): string {
  return NF_EUR.format(cents / 100);
}
function fmtSignedEur(cents: number): string {
  const sign = cents > 0 ? "+" : "";
  return sign + fmtEur(cents);
}
function fmtPct(p: number | null | undefined, digits = 1): string {
  if (p === null || p === undefined) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(digits)} %`;
}

// ============== Page ==============
export function DashboardPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe });
  const networth = useNetworthSummary();

  const username = me.data?.username ?? "...";

  return (
    <section className="space-y-4 md:space-y-5">
      <HeaderBar username={username} stichtag={networth.data?.latest?.stichtag} />

      {isMobile ? <NetworthChartCard /> : <HeroNetworth />}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        <NotgroschenCard />
        <SparrateCard />
        <InvestmentsCard />
        <RealEstateCard />
      </div>

      <OtterfolioCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <div className="lg:col-span-2">
          <PlanIstCard />
        </div>
        <AllokationMiniCard />
      </div>

      <p className="text-center text-xs" style={{ color: "var(--muted)" }}>
        {t("dashboard.build", { env: import.meta.env.MODE })}
      </p>
    </section>
  );
}

function OtterfolioCard() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["otterfolio", "overview"],
    queryFn: fetchOtterfolioOverview,
  });

  if (query.isLoading || !query.data) {
    return (
      <Link
        to="/otterfolio"
        className="fv-card p-5 block flex items-center gap-3 transition-colors hover:border-[var(--accent)]"
      >
        <img
          src="/otterfolio/otter-logo.png"
          alt=""
          width={44}
          height={44}
          style={{ borderRadius: 10, objectFit: "cover" }}
        />
        <div>
          <div className="fv-kpi-label">
            {t("dashboard.tiles.otterfolio", { defaultValue: "Otterfolio" })}
          </div>
          <div className="text-sm mt-0.5" style={{ color: "var(--muted)" }}>
            {t("dashboard.tiles.otterfolio_sub", { defaultValue: "Das Depot in guten Pfoten" })}
          </div>
        </div>
      </Link>
    );
  }

  const totalPos = query.data.returnCents >= 0;
  return (
    <Link
      to="/otterfolio"
      className="fv-card p-5 flex items-center gap-4 transition-colors hover:border-[var(--accent)]"
    >
      <img
        src="/otterfolio/otter-logo.png"
        alt=""
        width={56}
        height={56}
        style={{ borderRadius: 12, objectFit: "cover", flexShrink: 0 }}
      />
      <div className="flex-1 min-w-0">
        <div className="fv-kpi-label">
          {t("dashboard.tiles.otterfolio", { defaultValue: "Otterfolio" })}
        </div>
        <div className="text-2xl font-bold num mt-0.5">{fmtEur(query.data.totalValueCents)}</div>
        <div className="text-xs mt-1 num" style={{ color: "var(--muted)" }}>
          <span style={{ color: totalPos ? "var(--pos)" : "var(--neg)" }} className="font-medium">
            {fmtPct(query.data.returnPercent * 100, 1)}
          </span>{" "}
          · {query.data.positionsCount}{" "}
          {t("dashboard.tiles.otterfolio_positions", { defaultValue: "Positionen" })}
        </div>
      </div>
    </Link>
  );
}

// ============== Header ==============
function HeaderBar({ username, stichtag }: { username: string; stichtag?: string }) {
  const { t } = useTranslation();
  const now = new Date();
  const hour = now.getHours();
  const greeting =
    hour < 11
      ? t("dashboard.greeting_morning", { defaultValue: "Guten Morgen" })
      : hour < 18
        ? t("dashboard.greeting_day", { defaultValue: "Hallo" })
        : t("dashboard.greeting_evening", { defaultValue: "Guten Abend" });

  const today = now.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const snapStr = stichtag
    ? new Date(stichtag).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div
          className="text-xs font-semibold uppercase tracking-wider mb-1"
          style={{ color: "var(--muted)" }}
        >
          {t("nav.dashboard")}
        </div>
        <h1 className="text-2xl font-bold tracking-tight">
          {greeting}, {username}
        </h1>
        <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
          {t("dashboard.subtitle", {
            defaultValue: "Stand: {{today}}{{snap}}",
            today,
            snap: snapStr
              ? ` · ${t("dashboard.last_snapshot", { defaultValue: "letzter Snapshot {{date}}", date: snapStr })}`
              : "",
          })}
        </div>
      </div>
    </div>
  );
}

// ============== Hero (Networth + Chart) ==============
const PERIODS: NetworthPeriod[] = ["1M", "3M", "6M", "YTD", "1J", "3J", "5J", "all"];
const PERIOD_LABEL_KEY: Record<NetworthPeriod, string> = {
  "1M": "dashboard.networth_chart.period_1m",
  "3M": "dashboard.networth_chart.period_3m",
  "6M": "dashboard.networth_chart.period_6m",
  YTD: "dashboard.networth_chart.period_ytd",
  "1J": "dashboard.networth_chart.period_1y",
  "3J": "dashboard.networth_chart.period_3y",
  "5J": "dashboard.networth_chart.period_5y",
  all: "dashboard.networth_chart.period_all",
};

function HeroNetworth() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<NetworthPeriod>("1J");
  const summary = useNetworthSummary();
  const history = useNetworthHistory(period);

  const latestCents = summary.data?.latest?.totalCents ?? 0;

  // Filter 0-€-days from chart (per UX feedback)
  const chartData = useMemo(() => {
    const points = history.data?.history ?? [];
    return points.filter((p) => p.totalNetCents > 0);
  }, [history.data]);

  const firstCents = chartData[0]?.totalNetCents ?? latestCents;
  const change = latestCents - firstCents;
  const changePct = firstCents > 0 ? (change / firstCents) * 100 : null;

  return (
    <div className="fv-card p-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-5">
          <div className="fv-kpi-label mb-1">
            {t("dashboard.tiles.networth", { defaultValue: "Nettovermögen" })}
          </div>
          <div className="text-4xl font-bold tracking-tight num">
            {summary.isLoading ? "…" : fmtEur(latestCents)}
          </div>
          {chartData.length >= 2 && (
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className={`fv-pill ${change >= 0 ? "fv-pill-pos" : "fv-pill-neg"}`}>
                {change >= 0 ? "↑" : "↓"} {fmtSignedEur(change)}{" "}
                {changePct !== null && `(${fmtPct(changePct)})`}
              </span>
              <span className="text-xs num" style={{ color: "var(--muted)" }}>
                {t(PERIOD_LABEL_KEY[period])}
              </span>
            </div>
          )}
          {summary.data?.isStale && summary.data.daysSinceLastSnapshot !== null && (
            <div className="mt-3">
              <span className="fv-pill fv-pill-warn">
                {t("dashboard.tiles.networth_stale", { days: summary.data.daysSinceLastSnapshot })}
              </span>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="flex justify-end gap-1 mb-2">
            {PERIODS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`fv-chip ${period === p ? "fv-chip-active" : ""}`}
                style={period === p ? { background: "var(--text)", color: "var(--bg)" } : undefined}
              >
                {t(PERIOD_LABEL_KEY[p])}
              </button>
            ))}
          </div>
          <div style={{ position: "relative", height: 160, width: "100%" }}>
            {chartData.length < 2 ? (
              <div
                className="h-full flex items-center justify-center text-sm"
                style={{ color: "var(--muted)" }}
              >
                {t("dashboard.networth_chart.empty")}
              </div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="stichtag"
                    tickFormatter={formatStichtagShort}
                    tick={{ fontSize: 11, fill: "var(--muted)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<HeroTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="totalNetCents"
                    stroke="var(--accent)"
                    fill="url(#heroGrad)"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatStichtagShort(s: string): string {
  const parts = s.split("-");
  return `${parts[2]}.${parts[1]}`;
}

function HeroTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.[0]) return null;
  const value = payload[0].value as number;
  const label = (payload[0].payload as { stichtag: string }).stichtag;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-md"
      style={{
        background: "var(--card)",
        border: "1px solid var(--border)",
        color: "var(--text)",
      }}
    >
      <div style={{ color: "var(--muted)" }}>{label}</div>
      <div className="num font-semibold mt-0.5">{fmtEur(value)}</div>
    </div>
  );
}

// ============== Notgroschen Card ==============
function NotgroschenCard() {
  const { t } = useTranslation();
  const accounts = useAccountSummary();
  const cashflow = useCashflowSummary();

  const liquideCents = accounts.data?.byLiquidity.LIQUID?.totalCents ?? 0;
  const monthlyExpenseCents = cashflow.data?.smoothed.expenseCents ?? 0;
  const targetMonths = 3;
  const targetCents = monthlyExpenseCents * targetMonths;
  const monthsCovered = monthlyExpenseCents > 0 ? liquideCents / monthlyExpenseCents : 0;
  const ratio = monthlyExpenseCents > 0 ? Math.min(1, liquideCents / targetCents) : 0;
  const gapCents = targetCents - liquideCents;
  const isOk = monthsCovered >= targetMonths;

  const isLoading = accounts.isLoading || cashflow.isLoading;

  return (
    <div className="fv-card p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="fv-kpi-label">
          {t("dashboard.notgroschen", { defaultValue: "Notgroschen" })}
        </div>
        {!isLoading && monthlyExpenseCents > 0 && (
          <span className={`fv-pill ${isOk ? "fv-pill-pos" : "fv-pill-warn"}`}>
            {isOk
              ? t("common.ok", { defaultValue: "OK" })
              : t("dashboard.gap", { defaultValue: "Lücke" })}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="text-2xl font-bold num">…</div>
      ) : monthlyExpenseCents === 0 ? (
        <>
          <div className="text-2xl font-bold num">{fmtEur(liquideCents)}</div>
          <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
            {t("dashboard.notgroschen_no_expense", {
              defaultValue: "Monatsausgaben unbekannt — lege Cashflow-Positionen an",
            })}
          </div>
        </>
      ) : (
        <>
          <div className="text-2xl font-bold num">
            {monthsCovered.toFixed(1).replace(".", ",")}{" "}
            <span className="text-base font-medium" style={{ color: "var(--muted)" }}>
              / {targetMonths} {t("dashboard.months", { defaultValue: "Mon." })}
            </span>
          </div>
          <div
            className="mt-3 h-2 rounded-full overflow-hidden"
            style={{ background: "var(--border-soft)" }}
          >
            <div
              style={{
                width: `${(ratio * 100).toFixed(0)}%`,
                height: "100%",
                background: isOk ? "var(--pos)" : "var(--warn)",
              }}
            />
          </div>
          <div className="text-xs mt-2 num" style={{ color: "var(--muted)" }}>
            {fmtEur(liquideCents)} {t("dashboard.of", { defaultValue: "von" })}{" "}
            {fmtEur(targetCents)} ·{" "}
            {isOk ? (
              <span style={{ color: "var(--pos)" }} className="font-medium">
                +{fmtEur(-gapCents)}
              </span>
            ) : (
              <span style={{ color: "var(--warn)" }} className="font-medium">
                −{fmtEur(gapCents)}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============== Sparrate Card ==============
function SparrateCard() {
  const { t } = useTranslation();
  const cashflow = useCashflowSummary();

  const incomeCents = cashflow.data?.smoothed.incomeCents ?? 0;
  const expenseCents = cashflow.data?.smoothed.expenseCents ?? 0;
  const plannedSavingsCents = incomeCents - expenseCents;
  const sparquote = incomeCents > 0 ? (plannedSavingsCents / incomeCents) * 100 : 0;
  const goalPct = 30;
  const ratio = goalPct > 0 ? Math.min(1, sparquote / goalPct) : 0;
  const isOk = sparquote >= goalPct;

  return (
    <Link
      to="/cashflow/savings-rate"
      className="fv-card p-5 block transition-colors hover:border-[var(--accent)]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="fv-kpi-label">
          {t("dashboard.sparrate", { defaultValue: "Sparrate (Schnitt)" })}
        </div>
        {cashflow.data && incomeCents > 0 && (
          <span className={`fv-pill ${isOk ? "fv-pill-pos" : "fv-pill-warn"}`}>
            {isOk
              ? t("dashboard.goal_reached", { defaultValue: "Ziel ✓" })
              : t("dashboard.below_goal", { defaultValue: "unter Ziel" })}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold num">
        {cashflow.isLoading ? "…" : fmtEur(plannedSavingsCents)}
      </div>
      <div
        className="mt-3 h-2 rounded-full overflow-hidden"
        style={{ background: "var(--border-soft)" }}
      >
        <div
          style={{
            width: `${(ratio * 100).toFixed(0)}%`,
            height: "100%",
            background: isOk ? "var(--pos)" : "var(--accent)",
          }}
        />
      </div>
      <div className="text-xs mt-2 num" style={{ color: "var(--muted)" }}>
        {sparquote.toFixed(1).replace(".", ",")} %{" "}
        {t("dashboard.savings_rate_label", { defaultValue: "Sparquote" })} ·{" "}
        {t("dashboard.goal", { defaultValue: "Ziel" })} {goalPct} %
      </div>
    </Link>
  );
}

// ============== Investments Card ==============
function InvestmentsCard() {
  const { t } = useTranslation();
  const summary = useInvestmentsSummary();

  if (summary.isLoading) {
    return (
      <div className="fv-card p-5">
        <div className="fv-kpi-label mb-3">{t("dashboard.tiles.investments")}</div>
        <div className="text-2xl font-bold num">…</div>
      </div>
    );
  }
  if (!summary.data || summary.data.positionCount === 0) {
    return (
      <Link to="/investments" className="fv-card p-5 block">
        <div className="fv-kpi-label mb-3">{t("dashboard.tiles.investments")}</div>
        <div className="text-2xl font-bold num" style={{ color: "var(--muted)" }}>
          —
        </div>
        <div className="text-xs mt-2" style={{ color: "var(--accent)" }}>
          {t("dashboard.tiles.investments_empty_cta")}
        </div>
      </Link>
    );
  }

  const d = summary.data;
  const dailyPos = d.dailyChangeEur >= 0;
  const totalPos = d.totalGainLossEur >= 0;

  return (
    <Link
      to="/investments"
      className="fv-card p-5 block transition-colors hover:border-[var(--accent)]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="fv-kpi-label">{t("dashboard.tiles.investments")}</div>
        {d.dailyChangePercent !== null && (
          <span className={`fv-pill ${dailyPos ? "fv-pill-pos" : "fv-pill-neg"}`}>
            {dailyPos ? "↑" : "↓"} {fmtPct(d.dailyChangePercent, 2)}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold num">{fmtEur(d.totalMarketValueEur)}</div>
      <div className="text-xs mt-2 num" style={{ color: "var(--muted)" }}>
        {t("dashboard.since_buy", { defaultValue: "seit Kauf" })}{" "}
        <span style={{ color: totalPos ? "var(--pos)" : "var(--neg)" }} className="font-medium">
          {fmtPct(d.totalGainLossPercent, 2)}
        </span>{" "}
        · {fmtSignedEur(d.totalGainLossEur)}
      </div>
      {d.hasStale && (
        <div className="mt-2">
          <span className="fv-pill fv-pill-warn">{t("dashboard.tiles.investments_stale")}</span>
        </div>
      )}
    </Link>
  );
}

// ============== Real Estate Card ==============
function RealEstateCard() {
  const { t } = useTranslation();
  const summary = useRealEstatePortfolioSummary();

  if (summary.isLoading) {
    return (
      <div className="fv-card p-5">
        <div className="fv-kpi-label mb-3">{t("dashboard.tiles.realestate")}</div>
        <div className="text-2xl font-bold num">…</div>
      </div>
    );
  }
  if (!summary.data || summary.data.propertyCount === 0) {
    return (
      <Link to="/realestate" className="fv-card p-5 block">
        <div className="fv-kpi-label mb-3">{t("dashboard.tiles.realestate")}</div>
        <div className="text-2xl font-bold num" style={{ color: "var(--muted)" }}>
          —
        </div>
        <div className="text-xs mt-2" style={{ color: "var(--accent)" }}>
          {t("dashboard.tiles.realestate_empty_cta")}
        </div>
      </Link>
    );
  }

  const d = summary.data;

  return (
    <Link
      to="/realestate"
      className="fv-card p-5 block transition-colors hover:border-[var(--accent)]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="fv-kpi-label">{t("dashboard.tiles.realestate")}</div>
        <span
          className="fv-pill"
          style={{ background: "var(--border-soft)", color: "var(--muted)" }}
        >
          {t("dashboard.equity_short", { defaultValue: "EK" })}
        </span>
      </div>
      <div className="text-2xl font-bold num">{fmtEur(d.totalEquityCents)}</div>
      <div className="text-xs mt-2 num" style={{ color: "var(--muted)" }}>
        {t("dashboard.market_value", { defaultValue: "Marktwert" })}{" "}
        <b style={{ color: "var(--text)" }}>{fmtEur(d.totalMarketValueCents)}</b>
      </div>
      {d.hasStaleValuation && (
        <div className="mt-2">
          <span className="fv-pill fv-pill-warn">{t("dashboard.tiles.realestate_stale")}</span>
        </div>
      )}
    </Link>
  );
}

// ============== Plan vs Ist Card ==============
function PlanIstCard() {
  const { t } = useTranslation();
  const summary = useCashflowSummary();

  if (summary.isLoading) {
    return (
      <div className="fv-card p-5">
        <div className="fv-kpi-label">…</div>
      </div>
    );
  }
  if (!summary.data) return null;

  const { month, actualMonth, smoothed } = summary.data;
  const planSaldoCents = actualMonth.plannedCents;
  const istSaldoCents = actualMonth.actualCents;
  const varianceSaldo = actualMonth.varianceCents;

  const incomePct =
    smoothed.incomeCents > 0
      ? Math.min(1, Math.max(0, actualMonth.actualCents / smoothed.incomeCents))
      : 0;

  const monthLabel = formatMonthLabel(month);

  return (
    <div className="fv-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="fv-kpi-label">
            {t("dashboard.current_month", { defaultValue: "Aktueller Monat" })} · {monthLabel}
          </div>
          <div className="text-base font-semibold mt-1">
            {t("dashboard.plan_vs_ist", { defaultValue: "Plan vs. Ist" })}
          </div>
        </div>
        <Link to="/budget" className="text-xs font-medium" style={{ color: "var(--muted)" }}>
          {t("dashboard.to_budget", { defaultValue: "Zum Budget →" })}
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <PlanIstColumn
          label={t("dashboard.einnahmen", { defaultValue: "Einnahmen (Plan/Mon.)" })}
          mainValueCents={smoothed.incomeCents}
          subValueCents={null}
          subLabel={t("dashboard.smoothed_avg", { defaultValue: "Schnitt 12 Mon." })}
          progressPct={1}
          color="var(--pos)"
        />
        <PlanIstColumn
          label={t("dashboard.ausgaben", { defaultValue: "Ausgaben (Plan/Mon.)" })}
          mainValueCents={smoothed.expenseCents}
          subValueCents={null}
          subLabel={t("dashboard.smoothed_avg", { defaultValue: "Schnitt 12 Mon." })}
          progressPct={smoothed.incomeCents > 0 ? smoothed.expenseCents / smoothed.incomeCents : 0}
          color="var(--accent)"
        />
        <PlanIstColumn
          label={t("dashboard.saldo", { defaultValue: "Saldo aktuell" })}
          mainValueCents={planSaldoCents}
          subValueCents={istSaldoCents}
          subLabel={`${t("dashboard.ist", { defaultValue: "Ist" })} · Δ ${fmtSignedEur(varianceSaldo)}`}
          progressPct={incomePct}
          color={planSaldoCents >= 0 ? "var(--pos)" : "var(--neg)"}
        />
      </div>
    </div>
  );
}

function PlanIstColumn({
  label,
  mainValueCents,
  subValueCents,
  subLabel,
  progressPct,
  color,
}: {
  label: string;
  mainValueCents: number;
  subValueCents: number | null;
  subLabel: string;
  progressPct: number;
  color: string;
}) {
  return (
    <div>
      <div className="text-xs mb-1" style={{ color: "var(--muted)" }}>
        {label}
      </div>
      <div
        className="text-lg font-semibold num"
        style={{ color: mainValueCents < 0 ? "var(--neg)" : "var(--text)" }}
      >
        {fmtEur(mainValueCents)}
      </div>
      <div
        className="mt-2 h-1.5 rounded-full overflow-hidden"
        style={{ background: "var(--border-soft)" }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, progressPct * 100)).toFixed(0)}%`,
            height: "100%",
            background: color,
          }}
        />
      </div>
      <div className="text-[11px] mt-1 num" style={{ color: "var(--muted)" }}>
        {subValueCents !== null && (
          <>
            <span className="font-medium" style={{ color: "var(--text)" }}>
              {fmtEur(subValueCents)}
            </span>
            <span> · </span>
          </>
        )}
        {subLabel}
      </div>
    </div>
  );
}

function formatMonthLabel(monthStr: string): string {
  const [y, m] = monthStr.split("-");
  if (!y || !m) return monthStr;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

// ============== Allokation Mini ==============
const ALLOC_COLORS: Record<string, string> = {
  LIQUIDE: "#10b981",
  ANLAGEN: "#f59e0b",
  IMMOBILIE: "#3b82f6",
  SONSTIGE: "#94a3b8",
};

function AllokationMiniCard() {
  const { t } = useTranslation();
  const allocation = useAllocation();

  const items = allocation.data?.top.items ?? [];
  const total = allocation.data?.top.totalCents ?? 0;

  const segments = items
    .map((it) => ({
      key: it.key,
      label: t(`networth.classes.${it.key}`, { defaultValue: it.key }),
      pct: total > 0 ? (it.actualCents / total) * 100 : 0,
      driftPp: it.driftPp ?? null,
      color: ALLOC_COLORS[it.key] ?? "#94a3b8",
    }))
    .filter((s) => s.pct > 0.05);

  // biggest drift
  const biggestDrift = segments.reduce<{ key: string; pp: number } | null>((acc, s) => {
    if (s.driftPp === null) return acc;
    if (!acc || Math.abs(s.driftPp) > Math.abs(acc.pp)) return { key: s.key, pp: s.driftPp };
    return acc;
  }, null);

  return (
    <Link
      to="/allocation"
      className="fv-card p-5 block transition-colors hover:border-[var(--accent)]"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="fv-kpi-label">{t("nav.allocation")}</div>
        <span className="text-xs font-medium" style={{ color: "var(--muted)" }}>
          {t("dashboard.details", { defaultValue: "Details →" })}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <div style={{ position: "relative", width: 96, height: 96 }}>
          {segments.length > 0 ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={segments}
                  dataKey="pct"
                  innerRadius={28}
                  outerRadius={46}
                  paddingAngle={2}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {segments.map((s) => (
                    <Cell key={s.key} fill={s.color} stroke="var(--card)" strokeWidth={2} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div
              className="h-full flex items-center justify-center text-xs"
              style={{ color: "var(--muted)" }}
            >
              —
            </div>
          )}
        </div>
        <div className="flex-1 space-y-1.5 text-xs min-w-0">
          {segments.slice(0, 4).map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 truncate">
                <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="num font-medium shrink-0">{s.pct.toFixed(1)} %</span>
            </div>
          ))}
        </div>
      </div>
      {biggestDrift && Math.abs(biggestDrift.pp) >= 1 && (
        <div
          className="mt-3 pt-3 border-t text-[11px] num"
          style={{
            borderColor: "var(--border)",
            color: biggestDrift.pp >= 0 ? "var(--pos)" : "var(--warn)",
          }}
        >
          {t("dashboard.drift", { defaultValue: "Drift" })}{" "}
          {t(`networth.classes.${biggestDrift.key}`, { defaultValue: biggestDrift.key })}{" "}
          {biggestDrift.pp > 0 ? "+" : ""}
          {biggestDrift.pp.toFixed(1)} pp
        </div>
      )}
    </Link>
  );
}
